import { useMemo, useState } from "react";
import styles from "./SourcingIntelligence.module.css";
import { parseINR } from "./SourcingIntelligence";
import DSelectInput from "../../../components/Dashboard/DashboardInputs/DSelectInput";
import {
  ResponsiveContainer,
  AreaChart,
  BarChart,
  Area,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";

function monthKey(isoDate) {
  if (!isoDate) return null;
  const s = String(isoDate);
  return s.length >= 7 ? s.slice(0, 7) : null; // YYYY-MM
}

function monthLabel(key) {
  if (!key) return "";
  const [y, m] = key.split("-").map(Number);
  if (!y || !m) return key;
  const d = new Date(y, m - 1, 1);
  return d.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
}

function formatCompactINR(v) {
  if (v >= 10000000) return `₹${(v / 10000000).toFixed(2)} Cr`;
  if (v >= 100000) return `₹${(v / 100000).toFixed(2)} L`;
  return `₹${Number(v).toLocaleString("en-IN")}`;
}

export default function SpecTrendsPanel({ allYears, activeYearData }) {
  const specs = useMemo(() => {
    const list = (activeYearData?.itemwise || []).map((i) => i?.specification).filter(Boolean);
    return [...new Set(list)];
  }, [activeYearData]);

  const options = useMemo(() => {
    return {
      tokens: specs.map((_, idx) => String(idx)),
      visibleOption: specs.map((s) => (s.length > 120 ? `${s.slice(0, 120)}…` : s)),
    };
  }, [specs]);

  const [selectedSpecIdx, setSelectedSpecIdx] = useState("");
  const [selectedSpecLabel, setSelectedSpecLabel] = useState("");
  const [view, setView] = useState("monthly"); // monthly | yearly

  const selectedSpec = useMemo(() => {
    const idx = Number(selectedSpecIdx);
    if (!Number.isFinite(idx) || idx < 0 || idx >= specs.length) return "";
    return specs[idx] || "";
  }, [selectedSpecIdx, specs]);

  const series = useMemo(() => {
    if (!selectedSpec) return [];

    const monthlyMap = new Map();
    const yearlyMap = new Map();

    for (const y of allYears || []) {
      const yearNum = Number(y?.year);
      for (const row of y?.itemwise || []) {
        if (row?.specification !== selectedSpec) continue;

        const unit = parseINR(row?.unit_price);
        const qtyRaw = Number(row?.quantity);
        const weight = qtyRaw > 0 ? qtyRaw : 1;

        const mKey = monthKey(row?.po_date);
        if (mKey) {
          const agg =
            monthlyMap.get(mKey) || {
              period: mKey,
              label: monthLabel(mKey),
              unitSum: 0,
              wSum: 0,
              qty: 0,
              poCount: 0,
            };
          agg.unitSum += unit * weight;
          agg.wSum += weight;
          agg.qty += qtyRaw > 0 ? qtyRaw : 0;
          agg.poCount += 1;
          monthlyMap.set(mKey, agg);
        }

        if (yearNum) {
          const yKey = String(yearNum);
          const agg =
            yearlyMap.get(yKey) || {
              period: yKey,
              label: yKey,
              unitSum: 0,
              wSum: 0,
              qty: 0,
              poCount: 0,
            };
          agg.unitSum += unit * weight;
          agg.wSum += weight;
          agg.qty += qtyRaw > 0 ? qtyRaw : 0;
          agg.poCount += 1;
          yearlyMap.set(yKey, agg);
        }
      }
    }

    const toSeries = (m) =>
      [...m.values()]
        .map((a) => ({
          period: a.period,
          label: a.label,
          unitPrice: a.wSum > 0 ? a.unitSum / a.wSum : 0,
          quantity: a.qty,
          poCount: a.poCount,
        }))
        .sort((a, b) => (a.period > b.period ? 1 : -1));

    return view === "monthly" ? toSeries(monthlyMap) : toSeries(yearlyMap);
  }, [allYears, selectedSpec, view]);

  const summary = useMemo(() => {
    if (!series.length) return null;
    const prices = series.map((s) => Number(s.unitPrice || 0)).filter((n) => n > 0);
    const qtys = series.map((s) => Number(s.quantity || 0)).filter((n) => n >= 0);
    const minP = prices.length ? Math.min(...prices) : 0;
    const maxP = prices.length ? Math.max(...prices) : 0;
    const totalQty = qtys.reduce((a, b) => a + b, 0);
    const totalPOs = series.reduce((a, b) => a + (b.poCount || 0), 0);
    return { minP, maxP, totalQty, totalPOs };
  }, [series]);

  return (
    <div className={styles.specTrendsPanel}>
      <div className={styles.specTrendsHeader}>
        <div>
          <div className={styles.specTrendsTitle}>Specification Trends</div>
          <div className={styles.specTrendsSub}>
            Track unit price and quantity over time for the selected specification. Unit price uses qty-weighted average.
          </div>
        </div>
        <div className={styles.specTrendsControls}>
          <div className={styles.specTrendsSelect}>
            <DSelectInput
              label="Specification"
              options={options}
              value={selectedSpecLabel}
              getIDValue={true}
              customClass={styles.dSelectCompact}
              setIDValue={(tok) => setSelectedSpecIdx(Array.isArray(tok) ? tok?.[0] : tok)}
              onChange={(lbl) => setSelectedSpecLabel(lbl || "")}
              filterMatchFrom="any"
              filterLimit={400}
            />
          </div>
          <div className={styles.pillTabs}>
            <button
              type="button"
              className={`${styles.pillTab} ${view === "monthly" ? styles.pillTabActive : ""}`}
              onClick={() => setView("monthly")}
            >
              Monthly
            </button>
            <button
              type="button"
              className={`${styles.pillTab} ${view === "yearly" ? styles.pillTabActive : ""}`}
              onClick={() => setView("yearly")}
            >
              Yearly
            </button>
          </div>
        </div>
      </div>

      {!selectedSpec ? (
        <div className={styles.specTrendsEmpty}>Select a specification to view trends.</div>
      ) : !series.length ? (
        <div className={styles.specTrendsEmpty}>No trend data available for the selected specification.</div>
      ) : (
        <>
          <div className={styles.specTrendsSpecLine} title={selectedSpec}>
            <span className={styles.specTrendsSpecLabel}>Selected Spec:</span> {selectedSpec}
          </div>
          {summary && (
            <div className={styles.specTrendsKpis}>
              <div className={styles.specTrendsKpi}>
                <div className={styles.specTrendsKpiLbl}>Min Unit Price</div>
                <div className={styles.specTrendsKpiVal}>{formatCompactINR(summary.minP)}</div>
              </div>
              <div className={styles.specTrendsKpi}>
                <div className={styles.specTrendsKpiLbl}>Max Unit Price</div>
                <div className={styles.specTrendsKpiVal}>{formatCompactINR(summary.maxP)}</div>
              </div>
              <div className={styles.specTrendsKpi}>
                <div className={styles.specTrendsKpiLbl}>Total Quantity</div>
                <div className={styles.specTrendsKpiVal}>{Number(summary.totalQty).toLocaleString("en-IN")}</div>
              </div>
              <div className={styles.specTrendsKpi}>
                <div className={styles.specTrendsKpiLbl}>PO Events</div>
                <div className={styles.specTrendsKpiVal}>{Number(summary.totalPOs).toLocaleString("en-IN")}</div>
              </div>
            </div>
          )}

          <div className={styles.specTrendsChartsGrid}>
            <div className={styles.specTrendsChartCard}>
              <div className={styles.specTrendsChartTitle}>Unit Price Trend</div>
              <div className={styles.specTrendsChartSub}>Quantity-weighted average price</div>
              <div className={styles.specTrendsChartWrap}>
                <ResponsiveContainer width="100%" height={260}>
                  <AreaChart data={series} margin={{ top: 12, right: 16, left: 8, bottom: 0 }}>
                    <defs>
                      <linearGradient id="specUnitArea" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#60A5FA" stopOpacity={0.22} />
                        <stop offset="100%" stopColor="#60A5FA" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="#E6EEF6" strokeDasharray="4 4" vertical={false} />
                    <XAxis dataKey="label" tick={{ fill: "#546E7A", fontWeight: 800, fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis
                      tick={{ fill: "#90A4AE", fontWeight: 800, fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                      width={76}
                      tickFormatter={(v) => (v >= 100000 ? `${(v / 100000).toFixed(1)}L` : `${(v / 1000).toFixed(0)}K`)}
                    />
                    <Tooltip
                      content={({ active, payload, label }) => {
                        if (!active || !payload?.length) return null;
                        return (
                          <div className={styles.chartTooltip}>
                            <div className={styles.chartTooltipTitle}>{label}</div>
                            <div className={styles.chartTooltipVal}>{formatCompactINR(payload[0].value || 0)}</div>
                          </div>
                        );
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="unitPrice"
                      stroke="#60A5FA"
                      strokeWidth={2.2}
                      fill="url(#specUnitArea)"
                      dot={{ r: 3.2, strokeWidth: 2, fill: "#fff" }}
                      activeDot={{ r: 5, strokeWidth: 2, fill: "#fff" }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className={styles.specTrendsChartCard}>
              <div className={styles.specTrendsChartTitle}>Quantity Trend</div>
              <div className={styles.specTrendsChartSub}>Total quantity purchased</div>
              <div className={styles.specTrendsChartWrap}>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={series} margin={{ top: 12, right: 16, left: 8, bottom: 0 }}>
                    <defs>
                      <linearGradient id="specQtyBar" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#14B8A6" stopOpacity={0.95} />
                        <stop offset="100%" stopColor="#14B8A6" stopOpacity={0.55} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="#E6EEF6" strokeDasharray="4 4" vertical={false} />
                    <XAxis dataKey="label" tick={{ fill: "#546E7A", fontWeight: 800, fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: "#90A4AE", fontWeight: 800, fontSize: 11 }} axisLine={false} tickLine={false} width={58} />
                    <Tooltip
                      content={({ active, payload, label }) => {
                        if (!active || !payload?.length) return null;
                        return (
                          <div className={styles.chartTooltip}>
                            <div className={styles.chartTooltipTitle}>{label}</div>
                            <div className={styles.chartTooltipVal}>{Number(payload[0].value || 0).toLocaleString("en-IN")}</div>
                          </div>
                        );
                      }}
                    />
                    <Bar dataKey="quantity" fill="url(#specQtyBar)" radius={[10, 10, 0, 0]} maxBarSize={46} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
